import { t } from 'i18next'
import { Entitlement, Policies } from '~/src/shared/helpers/permissions'
import {
  POSWebviewHtmlMessage,
  POSWebviewMessage,
  PoliciesForAccessibleBases,
} from '~/src/shared/types'

const debugPosWebview = (): boolean => {
  // check for debug flag in url
  const urlParams = new URLSearchParams(window.location.search)
  return !!urlParams.get('debugPos')
}

export const isPOSWebview = (incomingUserAgent: string): boolean => {
  // check for debug flag in url
  if (debugPosWebview()) {
    return true
  }

  // check for POSWebview in user agent
  return incomingUserAgent.startsWith('POS Webview')
}

/*
  [2025-04 kp] currently the only type of message we send is html (POSWebviewHtmlMessage)
*/
const SendPOSWebviewMessage = (message: POSWebviewMessage): void => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const win = window as any
  if (win.webkit && win.webkit.messageHandlers && win.webkit.messageHandlers.toggleMessageHandler) {
    win.webkit.messageHandlers.toggleMessageHandler.postMessage({
      message,
    })
  } else if (win.ReactNativeWebView) {
    win.ReactNativeWebView.postMessage(
      JSON.stringify({
        message,
      })
    )
  } else {
    throw new Error('windows webkit/reactnative is not able to post message.')
  }
}

export const sendPOSWebviewHtmlMessage = (message: POSWebviewHtmlMessage): void => {
  // "print" html message to new window
  if (debugPosWebview()) {
    const childWin = window.open('about:blank') as Window
    childWin.document.write(message.data.html)
    childWin.document.close()
    childWin.focus()
    return
  }

  SendPOSWebviewMessage(message)
}

export const getUserRootPolicies = (
  policiesForAccessibleBases: PoliciesForAccessibleBases,
  rootBaseXRefID?: string
): {
  [policy in Policies]?: boolean
} => {
  return rootBaseXRefID
    ? Object.entries(policiesForAccessibleBases).reduce((acc, [policy, bases]) => {
        if (bases.includes(rootBaseXRefID)) {
          return {
            ...acc,
            [policy]: true,
          }
        }
        return acc
      }, {})
    : {}
}

// Enable groups-behaviour if the base has either RMM3 and/or POS_STAFF entitlements.
// This check will be deprecated once all customers have been upgraded
// i.e. all hierarchies have groups with POS_STAFF and RMM3 entitlement.
export const isVenueGroupingEnabledForClient = ({
  baseEntitlements,
}: {
  baseEntitlements: Entitlement[]
}): boolean => {
  if (
    baseEntitlements.includes(Entitlement.RMM3) ||
    baseEntitlements.includes(Entitlement.POS_STAFF)
  ) {
    return true
  }

  // For all legacy bases, do not show groups-behaviour
  return false
}

export const isVenueGroupingEnabledForServer = ({
  baseEntitlements,
}: {
  baseEntitlements: Entitlement[]
}): boolean => {
  if (
    baseEntitlements.includes(Entitlement.RMM3) ||
    baseEntitlements.includes(Entitlement.POS_STAFF)
  ) {
    return true
  }

  // For all legacy bases, do not show groups-behaviour
  return false
}

/**
 * Calculates a human readable string for pagination info
 * @param total pagination total pages
 * @param current pagination current page
 * @param limit pagination limit of results per page
 * @returns a human readable string. Eg: 1-3 of 14
 */
export const paginationShowEntriesText = (
  total: number,
  current: number,
  limit: number
): string => {
  return total === 0
    ? `${t('showEntries', {
        from: 0,
        to: 0,
        total: 0,
      })}`
    : `${t('showEntries', {
        from: current * limit + 1,
        to: (current + 1) * limit > total ? total : (current + 1) * limit,
        total,
      })}`
}

/**
 * Capitalize the first letter of the string, Eg: pending => Pending
 * @param value string
 * @returns Capitalized string
 */
export const capitalizeFirstLetter = (value: string): string => {
  return String(value).charAt(0).toUpperCase() + String(value).slice(1).toLocaleLowerCase()
}

/**
 * Escape a string so it matches literally inside a `RegExp`.
 *
 * Needed wherever user-typed text is interpolated into a pattern — a search box is the usual case.
 * Without it, `(` throws `SyntaxError: Unterminated group` when the RegExp is constructed, and `.`
 * silently matches every character instead of a full stop.
 *
 * Only for text going *into* a `RegExp`. Escaping a string that is then compared with `includes`
 * or `===` breaks it: the escape characters become part of the value being matched.
 *
 * @param value string to be used as a literal within a regular expression
 * @returns the string with every regex metacharacter backslash-escaped
 */
export const escapeRegExp = (value: string): string => {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
